import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Search, ChevronRight, Star, ArrowRight,
  QrCode, Utensils, Zap, Check,
} from 'lucide-react';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import { LANDING } from '@/constants/testIds';

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO = 'https://customer-assets-39nsmqrw.emergentagent.net/job_dining-portal-119/artifacts/5i4j17yj_ChatGPT_Image_Aug_31__2026__07_35_46_PM-removebg-preview.png';

const CRAVINGS = [
  { emoji: '🍕', label: 'Pizza',    q: 'pizza' },
  { emoji: '🍔', label: 'Burgers',  q: 'burger' },
  { emoji: '🍜', label: 'Noodles',  q: 'noodles' },
  { emoji: '🍱', label: 'Asian',    q: 'asian' },
  { emoji: '🍗', label: 'Chicken',  q: 'chicken' },
  { emoji: '🌮', label: 'Mexican',  q: 'mexican' },
  { emoji: '🍰', label: 'Desserts', q: 'dessert' },
  { emoji: '☕', label: 'Cafes',    q: 'cafe' },
  { emoji: '🥗', label: 'Healthy',  q: 'healthy' },
  { emoji: '🍛', label: 'Indian',   q: 'indian' },
];

const STEPS = [
  {
    Icon: QrCode,
    step: '01',
    title: 'Scan at your table',
    desc: 'Walk in, scan the QR code on your table, and you\'re in.',
  },
  {
    Icon: Utensils,
    step: '02',
    title: 'Browse & claim offers',
    desc: 'Explore live deals and claim an exclusive offer ID before you order.',
  },
  {
    Icon: Zap,
    step: '03',
    title: 'Show & save big',
    desc: 'Show your code to the waiter and enjoy the savings instantly.',
  },
];

const SAMPLE_REVIEWS = [
  { name: 'Priya S.',   stars: 5, text: 'Found an amazing 30% off deal at my favourite café. The app is so slick!', restaurant: 'Brewed Awakening' },
  { name: 'Rahul M.',   stars: 5, text: 'The QR scan flow is seamless. Claimed a BOGO offer within seconds.',        restaurant: 'The Spice Garden' },
  { name: 'Ananya K.',  stars: 4, text: 'Love that the full menu is on my phone. No more waiting around.',           restaurant: 'Green Bowl Co.' },
];

const VENDOR_FEATURES = [
  'QR-linked digital menu',
  'Live offer campaigns',
  'POS billing system',
  'Analytics & insights',
  'Customer feedback loop',
  'Multi-branch management',
];

const OFFER_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #c850c0 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

// Scroll-reveal wrapper — fires once when section enters viewport
function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function getDiscountLabel(offer) {
  if (offer.discount_value) {
    if (offer.discount_type === 'percent') return `${offer.discount_value}% OFF`;
    if (offer.discount_type === 'flat_amount') return `₹${offer.discount_value} OFF`;
  }
  if (offer.promotion_type === 'bogo')       return 'BOGO';
  if (offer.promotion_type === 'free_item')  return 'FREE ITEM';
  if (offer.promotion_type === 'combo')      return 'COMBO';
  if (offer.promotion_type === 'happy_hour') return 'HAPPY HOUR';
  return 'SPECIAL';
}

function OfferCard({ offer, idx, onNavigate }) {
  const gradient = OFFER_GRADIENTS[idx % OFFER_GRADIENTS.length];
  const label    = getDiscountLabel(offer);

  return (
    <div
      data-testid={LANDING.offerCard}
      onClick={() => onNavigate(`/offer/${offer.id}`)}
      className="group cursor-pointer rounded-2xl overflow-hidden bg-white border border-gray-100 hover:border-transparent hover:shadow-xl transition-shadow duration-300 hover:-translate-y-0.5"
      style={{ transform: 'translateZ(0)' }}
    >
      <div className="relative h-32 overflow-hidden" style={{ background: offer.image_url ? undefined : gradient }}>
        {offer.image_url && (
          <img
            src={offer.image_url}
            alt={offer.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}
        <span className="absolute top-2 left-2 bg-white/95 text-purple-700 font-black text-[10px] px-2 py-1 rounded-lg shadow-sm">
          {label}
        </span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />
      </div>
      <div className="p-3">
        <p className="text-[11px] text-gray-400 font-semibold truncate">{offer.restaurant_name || 'Restaurant'}</p>
        <h3 className="text-sm font-bold text-gray-900 leading-snug mt-0.5 line-clamp-2">{offer.title}</h3>
        {offer.offer_type && (
          <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wide text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
            {offer.offer_type.replace('_', ' ')}
          </span>
        )}
      </div>
    </div>
  );
}

function OfferSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-gray-100 animate-pulse">
      <div className="h-32 bg-gray-200" />
      <div className="p-3 space-y-2">
        <div className="h-2.5 bg-gray-200 rounded w-1/2" />
        <div className="h-4 bg-gray-200 rounded w-3/4" />
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, enterGuest } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [offers, setOffers]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [scrolled, setScrolled]       = useState(false);

  // Redirect authenticated users to their home
  useEffect(() => {
    if (user) {
      const path = user.role === 'admin'
        ? '/admin'
        : user.role === 'vendor'
          ? '/vendor/dashboard'
          : '/diner/discover';
      navigate(path, { replace: true });
    }
  }, [user, navigate]);

  // Navbar shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch public offers for the grid
  useEffect(() => {
    axios.get(`${API}/api/offers`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.offers || []);
        setOffers(list.slice(0, 6));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const googleAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`;

  const handleSearch = e => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      enterGuest();
      navigate(`/diner/discover?q=${encodeURIComponent(q)}`);
    }
  };

  const handleCraving = q => {
    enterGuest();
    navigate(`/diner/discover?q=${encodeURIComponent(q)}`);
  };

  const handleSeeAll = () => {
    enterGuest();
    navigate('/diner/discover');
  };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} className="min-h-screen bg-white overflow-x-hidden">

      {/* ─────────────────── NAVBAR ─────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 1px 0 rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src={LOGO}
              alt="Dine Around"
              className="h-9 w-auto object-contain"
              style={{ filter: scrolled ? 'none' : 'brightness(0) invert(1)' }}
            />
          </div>

          <div className="flex items-center gap-4">
            <a
              href="/vendor/login"
              className="hidden sm:block text-sm font-semibold transition-colors duration-200"
              style={{ color: scrolled ? '#6B7280' : 'rgba(255,255,255,0.75)' }}
            >
              For Restaurants
            </a>
            <a
              href={googleAuthUrl}
              data-testid={LANDING.dinerBtn}
              className="text-sm font-bold px-4 py-2 rounded-full transition-all duration-200 active:scale-95"
              style={
                scrolled
                  ? { background: '#5B21B6', color: '#fff' }
                  : { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }
              }
            >
              Sign In
            </a>
          </div>
        </div>
      </header>

      {/* ─────────────────── HERO ─────────────────── */}
      <section
        className="relative min-h-[88vh] flex flex-col items-center justify-center px-5 pt-24 pb-20 text-center overflow-hidden"
        style={{ background: 'linear-gradient(155deg, #1A0A2E 0%, #2B0A57 55%, #3D1278 100%)' }}
      >
        {/* Ambient glow blobs */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'rgba(124,58,237,0.22)', transform: 'translate(-50%,-50%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'rgba(91,33,182,0.18)', transform: 'translate(50%,50%)' }} />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 max-w-2xl mx-auto w-full"
        >
          {/* Live badge */}
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border border-white/15"
            style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/75 text-xs font-semibold tracking-wide">Live offers near you</span>
          </div>

          {/* Headline */}
          <h1
            style={{ fontFamily: "'Outfit', sans-serif" }}
            className="text-4xl sm:text-5xl lg:text-[3.5rem] font-black text-white leading-[1.1] tracking-tight mb-4"
          >
            Exclusive dining<br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #A78BFA, #DDD6FE)' }}
            >
              offers near you.
            </span>
          </h1>

          <p className="text-purple-200/75 text-base sm:text-lg mb-8 max-w-md mx-auto leading-relaxed">
            Scan. Browse. Claim. Discover curated deals from the best restaurants around you.
          </p>

          {/* Search bar */}
          <form
            onSubmit={handleSearch}
            className="relative max-w-lg mx-auto mb-5 flex items-center gap-2 bg-white rounded-2xl p-2"
            style={{ boxShadow: '0 20px 60px rgba(26,10,46,0.5)' }}
          >
            <div className="flex items-center flex-1 gap-2 pl-2 min-w-0">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                data-testid={LANDING.heroSearchInput}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search cuisine, restaurant…"
                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none font-medium min-w-0"
              />
            </div>
            <button
              data-testid={LANDING.heroSearchBtn}
              type="submit"
              className="flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}
            >
              Search
            </button>
          </form>

          {/* Auth CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-sm mx-auto sm:max-w-none">
            <a
              href={googleAuthUrl}
              data-testid={LANDING.googleAuthBtn}
              className="w-full sm:w-auto flex items-center justify-center gap-3 py-3.5 px-6 bg-white rounded-2xl font-bold text-sm text-gray-700 hover:shadow-2xl transition-shadow duration-300 active:scale-[0.98]"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </a>

            <button
              data-testid={LANDING.guestBtn}
              onClick={() => { enterGuest(); navigate('/diner/discover'); }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl border border-white/25 text-white/90 font-semibold text-sm hover:bg-white/10 transition-colors duration-200 active:scale-[0.98]"
            >
              Browse as Guest
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-4 text-purple-300/50 text-xs">
            Are you a restaurant owner?{' '}
            <a href="/vendor/login" className="text-purple-300/80 hover:text-white font-semibold transition-colors">Vendor Login</a>
          </p>
        </motion.div>

        {/* Scroll mouse indicator */}
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 opacity-30 pointer-events-none">
          <div className="w-5 h-8 rounded-full border-2 border-white/50 flex items-start justify-center pt-1.5">
            <motion.div
              className="w-1 h-1.5 bg-white rounded-full"
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1.6 }}
            />
          </div>
        </div>
      </section>

      {/* ─────────────────── CRAVINGS ─────────────────── */}
      <section className="py-10 bg-white border-b border-gray-100">
        <FadeIn>
          <div className="max-w-6xl mx-auto px-5">
            <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-5">
              What are you craving?
            </p>
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide snap-x snap-mandatory">
              {CRAVINGS.map(c => (
                <button
                  key={c.q}
                  data-testid={`${LANDING.cravingChip}-${c.q}`}
                  onClick={() => handleCraving(c.q)}
                  className="snap-start flex-shrink-0 flex flex-col items-center gap-1.5 w-16 group"
                >
                  <span
                    className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center text-2xl border border-gray-100 group-hover:border-purple-200 group-hover:bg-purple-50 transition-colors duration-200 group-hover:scale-105 transition-transform duration-200"
                  >
                    {c.emoji}
                  </span>
                  <span className="text-[11px] font-semibold text-gray-600 group-hover:text-purple-700 transition-colors duration-200">
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── LIVE OFFERS ─────────────────── */}
      <section className="py-14" style={{ background: '#F9F7FF' }}>
        <FadeIn>
          <div className="max-w-6xl mx-auto px-5">
            <div className="flex items-end justify-between mb-7">
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-purple-500 mb-1">On right now</p>
                <h2
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  className="text-2xl sm:text-3xl font-black text-gray-900"
                >
                  Live offers near you
                </h2>
              </div>
              {!loading && offers.length > 0 && (
                <button
                  data-testid={LANDING.seeAllOffersBtn}
                  onClick={handleSeeAll}
                  className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-purple-600 hover:text-purple-800 transition-colors duration-200"
                >
                  See all <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {loading
                ? [...Array(6)].map((_, i) => <OfferSkeleton key={i} />)
                : offers.length > 0
                  ? offers.map((offer, i) => (
                      <FadeIn key={offer.id || i} delay={i * 0.06}>
                        <OfferCard offer={offer} idx={i} onNavigate={navigate} />
                      </FadeIn>
                    ))
                  : (
                    <div className="col-span-2 sm:col-span-3 py-12 text-center text-gray-400 text-sm">
                      No live offers right now. Check back soon!
                    </div>
                  )
              }
            </div>

            {!loading && offers.length > 0 && (
              <div className="sm:hidden mt-6 text-center">
                <button
                  onClick={handleSeeAll}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-purple-600"
                >
                  See all offers <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── HOW IT WORKS ─────────────────── */}
      <section className="py-16 bg-white">
        <FadeIn>
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-10">
              <p className="text-xs font-bold tracking-widest uppercase text-purple-500 mb-2">The experience</p>
              <h2
                style={{ fontFamily: "'Outfit', sans-serif" }}
                className="text-2xl sm:text-3xl font-black text-gray-900"
              >
                Scan. Unlock. Enjoy.
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xs mx-auto">
                Three steps to your next great dining experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {STEPS.map(({ Icon, step, title, desc }, i) => (
                <FadeIn key={step} delay={i * 0.1}>
                  <div className="relative bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300 group">
                    <div
                      className="absolute -top-3 -left-1 text-6xl font-black select-none pointer-events-none"
                      style={{ fontFamily: "'Outfit', sans-serif", color: '#F3F0FF' }}
                    >
                      {step}
                    </div>
                    <div className="relative">
                      <div
                        className="w-11 h-11 rounded-2xl mb-4 flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm mb-1.5">{title}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── REVIEWS ─────────────────── */}
      <section
        className="py-16 overflow-hidden"
        style={{ background: 'linear-gradient(155deg, #1A0A2E 0%, #2B0A57 100%)' }}
      >
        <FadeIn>
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <h2
                style={{ fontFamily: "'Outfit', sans-serif" }}
                className="text-2xl sm:text-3xl font-black text-white"
              >
                Loved by diners
              </h2>
              <p className="text-purple-300/60 text-sm mt-2">1,200+ reviews and counting</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {SAMPLE_REVIEWS.map((review, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div
                    className="rounded-2xl p-5 border border-white/10 hover:bg-white/10 transition-colors duration-200"
                    style={{ background: 'rgba(255,255,255,0.07)' }}
                  >
                    <div className="flex gap-0.5 mb-3">
                      {[...Array(review.stars)].map((_, j) => (
                        <Star key={j} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed mb-4">"{review.text}"</p>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-sm">{review.name}</span>
                      <span className="text-purple-400/60 text-xs">{review.restaurant}</span>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── VENDOR SECTION ─────────────────── */}
      <section className="py-16 bg-white">
        <FadeIn>
          <div className="max-w-6xl mx-auto px-5">
            <div
              className="rounded-3xl overflow-hidden relative p-8 sm:p-12"
              style={{ background: 'linear-gradient(135deg, #F5F0FF 0%, #EDE9FF 100%)' }}
            >
              {/* Background glow */}
              <div
                className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
                style={{ background: '#7C3AED', transform: 'translate(30%, -30%)' }}
              />

              <div className="relative z-10 grid sm:grid-cols-2 gap-10 items-center">
                {/* Left copy */}
                <div>
                  <span className="inline-block text-xs font-bold tracking-widest uppercase text-purple-600 bg-purple-100 px-3 py-1 rounded-full mb-4">
                    For Restaurants
                  </span>
                  <h2
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    className="text-2xl sm:text-3xl font-black text-gray-900 mb-3 leading-tight"
                  >
                    Turn every table into<br />
                    <span style={{ color: '#5B21B6' }}>a loyal customer.</span>
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-sm">
                    Join hundreds of restaurants using Dine Around to run promotions, manage menus, and build lasting customer relationships.
                  </p>
                  <ul className="space-y-2.5 mb-7">
                    {VENDOR_FEATURES.map(f => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: '#5B21B6' }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/vendor/login"
                    data-testid={LANDING.vendorCtaBtn}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold text-white transition-opacity hover:opacity-90 active:scale-95"
                    style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}
                  >
                    Partner with us
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>

                {/* Right stats */}
                <div className="hidden sm:flex flex-col gap-3">
                  {[
                    { label: 'Average rating boost',  value: '+0.8★', sub: 'after 3 months on platform' },
                    { label: 'Offer redemption rate', value: '34%',   sub: 'industry average is 8%' },
                    { label: 'Monthly QR scans',      value: '12k+',  sub: 'per active restaurant' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100">
                      <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
                      <p
                        className="text-2xl font-black text-gray-900 mt-0.5"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {stat.value}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{stat.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="py-20 text-center" style={{ background: '#F9F7FF' }}>
        <FadeIn>
          <div className="max-w-2xl mx-auto px-5">
            <h2
              style={{ fontFamily: "'Outfit', sans-serif" }}
              className="text-3xl sm:text-4xl font-black text-gray-900 mb-3"
            >
              Ready to dine smarter?
            </h2>
            <p className="text-gray-500 mb-8 text-sm sm:text-base max-w-sm mx-auto">
              Join thousands of diners discovering exclusive offers every day.
            </p>
            <a
              href={googleAuthUrl}
              data-testid={LANDING.finalCtaBtn}
              className="inline-flex items-center gap-3 py-4 px-8 rounded-2xl font-bold text-white text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{ background: '#5B21B6', boxShadow: '0 12px 40px rgba(91,33,182,0.35)' }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Get started free
            </a>
            <p className="text-xs text-gray-400 mt-4">No credit card required · Sign in with Google</p>
          </div>
        </FadeIn>
      </section>

      {/* ─────────────────── FOOTER ─────────────────── */}
      <footer className="py-12" style={{ background: '#0D0518' }}>
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-8 mb-10">
            <div className="max-w-xs">
              <div className="flex items-center mb-3">
                <img
                  src={LOGO}
                  alt="Dine Around"
                  className="h-8 w-auto object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Discover exclusive dining offers, scan menus, and have a great experience at restaurants near you.
              </p>
            </div>

            <div className="flex gap-10 text-sm">
              <div>
                <p className="font-bold text-gray-200 mb-3 text-xs uppercase tracking-wide">Diners</p>
                <div className="flex flex-col gap-2.5 text-gray-400 text-xs">
                  <button
                    onClick={() => { enterGuest(); navigate('/diner/discover'); }}
                    className="hover:text-white transition-colors duration-200 text-left"
                  >
                    Discover
                  </button>
                  <a href="/login" className="hover:text-white transition-colors duration-200">Sign In</a>
                </div>
              </div>
              <div>
                <p className="font-bold text-gray-200 mb-3 text-xs uppercase tracking-wide">Partners</p>
                <div className="flex flex-col gap-2.5 text-gray-400 text-xs">
                  <a href="/vendor/login"          className="hover:text-white transition-colors duration-200">Vendor Login</a>
                  <a href="/register?role=vendor"  className="hover:text-white transition-colors duration-200">Get Listed</a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-gray-600 text-xs">
              © {new Date().getFullYear()} Dine Around. All rights reserved.
            </p>
            <div className="flex gap-5 text-xs text-gray-600">
              <a href="#" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
